if (!customElements.get('collection-showcase-products')) {
  customElements.define(
    'collection-showcase-products',
    class CollectionsShowcaseProducts extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        if (this.dataset.enableSlider !== 'true') {
          return;
        }

        this.initSlider();
      }

      initSlider() {
        const items = Number(this.dataset.items);
        // NHOI: on mobile show ~2 larger products (with a peek of the next) instead of 3 small ones.
        // Desktop (768px+) keeps the section's own items-per-row.
        const sliderOptions = {
          slidesPerView: 2.2,
          spaceBetween: 8,
          breakpoints: {
            768: {
              slidesPerView: items,
              spaceBetween: 20,
            },
          },
          threshold: 2,
        };

        this.sliderInstance = new window.FoxTheme.Carousel(this.querySelector('.swiper'), sliderOptions);
        this.sliderInstance.init();
      }
    }
  );
}

if (!customElements.get('collection-showcase')) {
  customElements.define(
    'collection-showcase',
    class CollectionsShowcase extends HTMLElement {
      constructor() {
        super();

        this.images = this.querySelectorAll('.collection-showcase__image');
        this.tabItems = this.querySelectorAll('.collection-showcase__tabs li a');
        this.mainContent = this.querySelector('.collection-showcase__contents');
        this.currentIndex = 0;
        this.hoverTracker = null;

        this.init();
      }

      init() {
        if (this.tabItems) {
          if ('ontouchstart' in window) {
            this.tabItems.forEach((item) => {
              item.addEventListener('touchstart', this.onTouchChange.bind(this));
              item.addEventListener('click', this.onClick.bind(this));
            });
          } else {
            this.tabItems.forEach((item) => {
              item.addEventListener('mouseover', (e) => this.onMouseOver(e));
              item.addEventListener('focus', (e) => this.onMouseOver(e));
            });
          }
        }

        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId != this.dataset.sectionId) return;
            const { target } = e;
            const index = Number(target.dataset.index);
            this.setActiveTab(index);
          });
        }

        this.initMobile();
      }

      // NHOI (2026-09-29): mobile behaves like one carousel with a single active collection.
      // - Tapping a collection name only switches the preview (image + products); it never navigates.
      // - One CTA inside the image ("Shop <active collection> →") is the one way through;
      //   its text and URL follow the active collection. (The footer button is hidden on mobile.)
      // - Auto-rotates every 5s while on screen; any tap/swipe pauses it for 12s.
      // Desktop (768px+) keeps the original hover behaviour and "Shop now" label.
      initMobile() {
        if (!this.tabItems || this.tabItems.length === 0) return;
        this.mql = window.matchMedia('(max-width: 767.98px)');
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.rotateDelay = 5000;
        this.pauseDelay = 12000;
        this.inView = false;

        this.tabItems.forEach((item) => {
          item.addEventListener(
            'click',
            (event) => {
              if (!this.mql.matches) return;
              event.preventDefault();
              event.stopImmediatePropagation();
              this.pauseRotation();
              this.setActiveTab(Number(item.dataset.index));
            },
            true
          );
        });

        const overlay = this.querySelector('.collection-showcase__tabs');
        if (overlay && !overlay.querySelector('.nhoi-showcase-cta')) {
          this.overlayCta = document.createElement('a');
          this.overlayCta.className = 'nhoi-showcase-cta btn btn--primary';
          overlay.appendChild(this.overlayCta);
        } else if (overlay) {
          this.overlayCta = overlay.querySelector('.nhoi-showcase-cta');
        }

        this.addEventListener('pointerdown', () => this.pauseRotation(), { passive: true });

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              this.inView = entries.some((entry) => entry.isIntersecting);
            },
            { threshold: 0.35 }
          );
          this.observer.observe(this);
        } else {
          this.inView = true;
        }

        const onChange = () => {
          this.updateMobileCta();
          this.startRotation();
        };
        if (this.mql.addEventListener) {
          this.mql.addEventListener('change', onChange);
        } else if (this.mql.addListener) {
          this.mql.addListener(onChange);
        }
        onChange();
      }

      startRotation() {
        clearInterval(this.rotateTimer);
        if (!this.mql || !this.mql.matches || this.reduceMotion || this.tabItems.length < 2) return;
        this.rotateTimer = setInterval(() => {
          if (document.hidden || !this.inView) return;
          this.setActiveTab((this.currentIndex + 1) % this.tabItems.length);
        }, this.rotateDelay);
      }

      pauseRotation() {
        if (!this.mql || !this.mql.matches) return;
        clearInterval(this.rotateTimer);
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => this.startRotation(), this.pauseDelay);
      }

      updateMobileCta() {
        const activeLink = this.tabItems && this.tabItems[this.currentIndex];
        if (this.overlayCta && activeLink) {
          this.overlayCta.textContent = `Shop ${activeLink.textContent.trim()} →`;
          this.overlayCta.setAttribute('href', activeLink.getAttribute('href'));
        }
        const content =
          this.mainContent &&
          this.mainContent.querySelector(`.collection-showcase__content[data-index="${this.currentIndex}"]`);
        const button = content && content.querySelector('.collection-showcase__footer .btn');
        if (!button) return;
        if (!button.dataset.nhoiLabel) button.dataset.nhoiLabel = button.textContent.trim();
        const link = this.tabItems[this.currentIndex];
        const title = link ? link.textContent.trim() : '';
        button.textContent = this.mql && this.mql.matches && title ? `Shop ${title} →` : button.dataset.nhoiLabel;
      }

      disconnectedCallback() {
        clearInterval(this.rotateTimer);
        clearTimeout(this.resumeTimer);
        if (this.observer) this.observer.disconnect();
      }

      isSelected(tab) {
        return tab.classList.contains('active');
      }

      onMouseOver(e) {
        let { target } = e;
        const index = Number(target.dataset.index);

        clearTimeout(this.hoverTracker);
        this.hoverTracker = setTimeout(() => {
          if (target.classList.contains('active')) return;

          this.setActiveTab(index);
        }, 100);
      }

      onTouchChange(e) {
        const { target } = e;
        const index = Number(target.dataset.index);

        if (this.isSelected(target)) {
          this.preventClick = false;
          return;
        } else {
          this.preventClick = true;
        }

        this.setActiveTab(index);
      }

      onClick(e) {
        if (this.preventClick) {
          e.preventDefault();
        }
      }

      setActiveTab(tabIndex) {
        if (this.currentIndex == tabIndex) {
          return;
        }

        let newTab, currentItem, newItem, currentImage, newImage;

        newImage = this.images[tabIndex];
        newImage.hidden = false; // Being load image.
        newImage.classList.add('active');

        currentImage = this.images[this.currentIndex];
        currentImage.classList.remove('active');

        currentItem = this.tabItems[this.currentIndex];
        newItem = this.tabItems[tabIndex];

        currentItem.classList.remove('active');
        newItem.classList.add('active');

        const collectionProducts = this.mainContent.querySelectorAll('.collection-showcase__content');
        collectionProducts.forEach((collection) => {
          collection.hidden = true;
          collection.classList.remove('active');
        });

        newTab = this.mainContent.querySelector(`.collection-showcase__content[data-index="${tabIndex}"]`);
        if (!newTab) {
          const template = this.mainContent.querySelector(`template[data-index="${tabIndex}"]`);
          newTab = document.importNode(template.content, true);
          this.mainContent.appendChild(newTab);
          newTab.hidden = false;
        } else {
          newTab.hidden = false;
        }

        const gridList = newTab.querySelector('grid-list');
        gridList && gridList.showGridItems();

        const motion = newTab.querySelector('motion-element');
        motion && motion.refreshAnimation();

        this.currentIndex = tabIndex;
        this.updateMobileCta && this.updateMobileCta();
      }
    }
  );
}